import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { useHistory, useLocation } from 'react-router-dom';
import Confetti from 'react-confetti';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton, SCUITextField } from '../../atomic/atoms';
import { useRetroPresence } from '../../services/signalr';
import { RetroStickyCard } from './RetroStickyCard';
import { useRetroSingleTabGuard } from './useRetroSingleTabGuard';
import './RetroBoard.scss';

const ROOM_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
const CURSOR_COLORS = ['#e11d48', '#2563eb', '#059669', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4f46e5'];

function colorForKey(key: string): string {
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        hash = Math.trunc(hash * 31 + (key.codePointAt(i) ?? 0));
    }
    return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
}

function memberLabel(displayName: string, userRef: string, _connectionId?: string): string {
    return displayName || userRef || 'user';
}

function readRoomFromQuery(search: string): string {
    const params = new URLSearchParams(search);
    return (params.get('room') || '').trim();
}

type RetroBoardProps = {
    /** true: SCUIContent sarmalayıcısı olmadan (ör. Daily Planner tab içinde) */
    embedded?: boolean;
};

export const RetroBoard: FC<RetroBoardProps> = ({ embedded = false }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const surfaceRef = useRef<HTMLDivElement>(null);
    const { isActiveTab, showTakeoverNotice, dismissTakeoverNotice } = useRetroSingleTabGuard(true);

    const [roomInput, setRoomInput] = useState(() => readRoomFromQuery(location.search) || 'demo-room');
    const [activeRoom, setActiveRoom] = useState(() => {
        const fromQuery = readRoomFromQuery(location.search);
        return fromQuery && ROOM_PATTERN.test(fromQuery) ? fromQuery : '';
    });
    const [roomError, setRoomError] = useState(false);
    const [editingCardId, setEditingCardId] = useState<string | null>(null);

    const {
        connectionState,
        isHubReady,
        members,
        cursors,
        localCursor,
        cards,
        confettiBurstId,
        lastConfetti,
        activeCount,
        isRoomAdmin,
        contentHidden,
        adminDisplayName,
        adminConnectionId,
        sendCursorPosition,
        addCard,
        updateCardText,
        moveCard,
        deleteCard,
        triggerConfetti,
        setContentHidden,
    } = useRetroPresence({
        roomId: activeRoom,
        enabled: !!activeRoom && isActiveTab,
        surfaceRef,
        trackCursor: true,
    });

    useEffect(() => {
        const fromQuery = readRoomFromQuery(location.search);
        if (fromQuery && ROOM_PATTERN.test(fromQuery) && fromQuery !== activeRoom) {
            setRoomInput(fromQuery);
            setActiveRoom(fromQuery);
        }
    }, [location.search]);

    const statusLabel = useMemo(() => {
        switch (connectionState) {
            case 'connected':
                return t('retroBoard.status.connected');
            case 'connecting':
                return t('retroBoard.status.connecting');
            case 'reconnecting':
                return t('retroBoard.status.reconnecting');
            default:
                return t('retroBoard.status.disconnected');
        }
    }, [connectionState, t]);

    const statusColor = useMemo(() => {
        switch (connectionState) {
            case 'connected':
                return 'success';
            case 'connecting':
            case 'reconnecting':
                return 'warning';
            default:
                return 'default';
        }
    }, [connectionState]);

    const handleJoin = useCallback(() => {
        const roomId = roomInput.trim();
        if (!ROOM_PATTERN.test(roomId)) {
            setRoomError(true);
            return;
        }
        setRoomError(false);
        setEditingCardId(null);
        setActiveRoom(roomId);
        const params = new URLSearchParams(location.search);
        params.set('room', roomId);
        if (embedded) {
            params.set('tab', 'retro');
        }
        history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
    }, [roomInput, history, location.pathname, location.search, embedded]);

    const handleLeave = useCallback(() => {
        setActiveRoom('');
        setEditingCardId(null);
        const params = new URLSearchParams(location.search);
        params.delete('room');
        if (embedded) {
            params.set('tab', 'retro');
        }
        const search = params.toString();
        history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    }, [history, location.pathname, location.search, embedded]);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!activeRoom) return;
            sendCursorPosition(e.clientX, e.clientY);
        },
        [activeRoom, sendCursorPosition]
    );

    const toNormalizedPoint = useCallback((clientX: number, clientY: number) => {
        const rect = surfaceRef.current?.getBoundingClientRect();
        if (!rect || rect.width <= 0 || rect.height <= 0) {
            return { x: 0.1, y: 0.1 };
        }
        return {
            x: Math.min(0.85, Math.max(0, (clientX - rect.left) / rect.width)),
            y: Math.min(0.85, Math.max(0, (clientY - rect.top) / rect.height)),
        };
    }, []);

    const handleAddCardClick = useCallback(async () => {
        if (!isHubReady) return;
        const point = toNormalizedPoint(
            (surfaceRef.current?.getBoundingClientRect().left || 0) + 80,
            (surfaceRef.current?.getBoundingClientRect().top || 0) + 80
        );
        try {
            const card = await addCard(point.x, point.y);
            setEditingCardId(card.id);
        } catch (err) {
            console.warn('[Retro] addCard failed', err);
        }
    }, [addCard, isHubReady, toNormalizedPoint]);

    const handleSurfaceDoubleClick = useCallback(
        async (e: React.MouseEvent<HTMLDivElement>) => {
            if (!activeRoom || !isHubReady) return;
            if ((e.target as HTMLElement).closest('.retro-sticky-card')) return;
            const point = toNormalizedPoint(e.clientX, e.clientY);
            try {
                const card = await addCard(point.x, point.y);
                setEditingCardId(card.id);
            } catch (err) {
                console.warn('[Retro] addCard failed', err);
            }
        },
        [activeRoom, addCard, isHubReady, toNormalizedPoint]
    );

    const handleConfettiClick = useCallback(async () => {
        if (!isHubReady) return;
        try {
            await triggerConfetti();
        } catch (err) {
            console.warn('[Retro] confetti failed', err);
        }
    }, [isHubReady, triggerConfetti]);

    const handleToggleContentHidden = useCallback(async () => {
        if (!isHubReady) return;
        try {
            await setContentHidden(!contentHidden);
        } catch (err) {
            console.warn('[Retro] setContentHidden failed', err);
        }
    }, [contentHidden, isHubReady, setContentHidden]);

    const cursorColor = contentHidden ? '#9ca3af' : undefined;

    const confettiFromLabel = lastConfetti
        ? memberLabel(lastConfetti.displayName, lastConfetti.userRef, lastConfetti.connectionId)
        : '';

    if (!isActiveTab) {
        return null;
    }

    const content = (
        <Box className={`retro-board-page ${embedded ? 'retro-board-page--embedded' : ''}`}>
            {showTakeoverNotice && (
                <Alert severity="info" onClose={dismissTakeoverNotice} className="retro-board-takeover-alert">
                    {t('retroBoard.otherTabTakeover')}
                </Alert>
            )}
            {confettiBurstId > 0 && (
                <Confetti
                    key={confettiBurstId}
                    className="retro-board-confetti"
                    width={window.innerWidth}
                    height={window.innerHeight}
                    numberOfPieces={280}
                    recycle={false}
                    gravity={0.22}
                />
            )}
            <Box className="retro-board-toolbar">
                <Box className="retro-board-toolbar__join">
                    <SCUITextField
                        stateName="roomId"
                        label={t('retroBoard.roomLabel')}
                        value={roomInput}
                        onChange={(value) => {
                            setRoomInput(value);
                            setRoomError(false);
                        }}
                        placeholder={t('retroBoard.roomPlaceholder')}
                        error={roomError}
                        helperText={roomError ? t('retroBoard.roomInvalid') : t('retroBoard.roomHint')}
                        disabled={!!activeRoom}
                        fullWidth
                        size="m"
                        variant="outlined"
                    />
                    {!activeRoom ? (
                        <SCUIButton
                            type="primary"
                            sizeType="default"
                            size="m"
                            text={t('retroBoard.join')}
                            onClick={handleJoin}
                            buttonType="button"
                        />
                    ) : (
                        <SCUIButton
                            type="secondary"
                            sizeType="default"
                            size="m"
                            text={t('retroBoard.leave')}
                            onClick={handleLeave}
                            buttonType="button"
                        />
                    )}
                    {activeRoom && (
                        <SCUIButton
                            type="primary"
                            sizeType="default"
                            size="m"
                            text={t('retroBoard.addCard')}
                            onClick={handleAddCardClick}
                            disabled={!isHubReady}
                            buttonType="button"
                        />
                    )}
                    {activeRoom && (
                        <SCUIButton
                            type={contentHidden ? 'primary' : 'secondary'}
                            sizeType="default"
                            size="m"
                            text={contentHidden ? t('retroBoard.showContent') : t('retroBoard.hideContent')}
                            onClick={handleToggleContentHidden}
                            disabled={!isHubReady}
                            buttonType="button"
                        />
                    )}
                    {activeRoom && (
                        <Tooltip title={t('retroBoard.confetti')}>
                            <span className="retro-board-confetti-trigger">
                                <IconButton
                                    className="retro-board-confetti-btn"
                                    color="secondary"
                                    onClick={handleConfettiClick}
                                    disabled={!isHubReady}
                                    aria-label={t('retroBoard.confetti')}
                                    size="large"
                                >
                                    <CelebrationIcon fontSize="inherit" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
                <Box className="retro-board-toolbar__meta">
                    <Chip size="small" color={statusColor as any} label={statusLabel} className="retro-board-status-chip" />
                    {activeRoom && (
                        <Typography variant="body2" className="retro-board-active-count">
                            {t('retroBoard.activeCount', { count: activeCount })}
                        </Typography>
                    )}
                    {activeRoom && isRoomAdmin && (
                        <Chip size="small" color="info" label={t('retroBoard.youAreAdmin')} className="retro-board-status-chip" />
                    )}
                    {activeRoom && !isRoomAdmin && adminDisplayName && (
                        <Typography variant="body2" className="retro-board-active-count">
                            {t('retroBoard.adminLabel', { name: adminDisplayName })}
                        </Typography>
                    )}
                    {activeRoom && contentHidden && (
                        <Chip size="small" color="warning" label={t('retroBoard.contentHiddenBadge')} className="retro-board-status-chip" />
                    )}
                    {activeRoom && confettiBurstId > 0 && confettiFromLabel && (
                        <Typography variant="body2" className="retro-board-confetti-by">
                            {t('retroBoard.confettiBy', { name: confettiFromLabel })}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box className="retro-board-layout">
                <Box
                    ref={surfaceRef}
                    className={`retro-board-surface ${activeRoom ? 'retro-board-surface--live' : ''}`}
                    onMouseMove={handleMouseMove}
                    onDoubleClick={handleSurfaceDoubleClick}
                >
                    {!activeRoom && (
                        <Box className="retro-board-surface__empty">
                            <Typography variant="h6">{t('retroBoard.emptyTitle')}</Typography>
                            <Typography variant="body2">{t('retroBoard.emptyDescription')}</Typography>
                        </Box>
                    )}
                    {cards.map((card) => (
                        <RetroStickyCard
                            key={card.id}
                            card={card}
                            contentHidden={contentHidden}
                            startEditing={editingCardId === card.id}
                            surfaceRef={surfaceRef}
                            onTextChange={(id, text) => {
                                void updateCardText(id, text);
                            }}
                            onMove={(id, x, y) => {
                                void moveCard(id, x, y);
                            }}
                            onDelete={(id) => {
                                void deleteCard(id);
                            }}
                            onEditFinished={(id) => {
                                setEditingCardId((current) => (current === id ? null : current));
                            }}
                        />
                    ))}

                    {localCursor && (
                        <Box
                            className={`retro-board-cursor retro-board-cursor--local ${contentHidden ? 'retro-board-cursor--hidden' : ''}`}
                            style={{
                                left: `${localCursor.x * 100}%`,
                                top: `${localCursor.y * 100}%`,
                                '--cursor-color': cursorColor || '#111827',
                            } as React.CSSProperties}
                        >
                            <span className="retro-board-cursor__pointer" />
                            {!contentHidden && (
                                <span className="retro-board-cursor__label">{t('retroBoard.you')}</span>
                            )}
                        </Box>
                    )}

                    {Object.values(cursors).map((cursor) => {
                        const key = cursor.userRef || cursor.displayName || cursor.connectionId;
                        return (
                            <Box
                                key={key}
                                className={`retro-board-cursor ${contentHidden ? 'retro-board-cursor--hidden' : ''}`}
                                style={{
                                    left: `${cursor.x * 100}%`,
                                    top: `${cursor.y * 100}%`,
                                    '--cursor-color': cursorColor || colorForKey(key),
                                } as React.CSSProperties}
                            >
                                <span className="retro-board-cursor__pointer" />
                                {!contentHidden && (
                                    <span className="retro-board-cursor__label">
                                        {memberLabel(cursor.displayName, cursor.userRef, cursor.connectionId)}
                                    </span>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                <Box className="retro-board-sidebar">
                    <Typography variant="subtitle2" className="retro-board-sidebar__title">
                        {t('retroBoard.membersTitle')}
                    </Typography>
                    {!activeRoom && (
                        <Typography variant="body2" color="text.secondary">
                            {t('retroBoard.membersEmpty')}
                        </Typography>
                    )}
                    {activeRoom && members.length === 0 && connectionState === 'connected' && (
                        <Typography variant="body2" color="text.secondary">
                            {t('retroBoard.membersWaiting')}
                        </Typography>
                    )}
                    <ul className="retro-board-members">
                        {members.map((member) => {
                            const key = member.userRef || member.displayName || member.connectionId;
                            const memberIsAdmin =
                                (!!adminDisplayName &&
                                    !!member.displayName &&
                                    adminDisplayName.toLowerCase() === member.displayName.toLowerCase()) ||
                                (!!adminConnectionId && member.connectionId === adminConnectionId);
                            return (
                                <li key={key} className="retro-board-members__item">
                                    <span
                                        className="retro-board-members__dot"
                                        style={{ backgroundColor: contentHidden ? '#9ca3af' : colorForKey(key) }}
                                    />
                                    <span className="retro-board-members__name">
                                        {memberLabel(member.displayName, member.userRef, member.connectionId)}
                                        {memberIsAdmin ? ` · ${t('retroBoard.adminBadge')}` : ''}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </Box>
            </Box>
        </Box>
    );

    if (embedded) {
        return content;
    }

    return (
        <SCUIContent headerName={t('retroBoard.title')}>
            {content}
        </SCUIContent>
    );
};

export default RetroBoard;
